import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Briefcase,
  Calendar,
  CheckCircle2,
  CreditCard,
  Edit,
  Flag,
  Globe,
  Languages,
  MapPin,
  MessageSquare,
  Phone,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { fileToBase64 } from "../../../api/authService";
import professionalProfileApi from "../../../api/professionalProfile";
import CustomLoader from "../../../components/common/CustomLoader";
import useTawkControl from "../../../hooks/useTawkControl";
import { commaSeparatedTitleCase, titleCase } from "../../../utils/helpers";
import PersonalInfoForm from "./PersonalInfoForm";
import { BASE_URL } from "../../../config";
import * as Sentry from '@sentry/react'
import { useAuthStore } from "../../../store/authStore.js";

const transformString = (text = "") => text.replaceAll("_", " ");

const InfoRow = ({ icon: Icon, label, value }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        borderRadius: 2,
        transition: "background 0.15s",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box
        sx={{
          mt: "2px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: "grey.100",
          color: "text.secondary",
          flexShrink: 0,
        }}
      >
        <Icon size={15} strokeWidth={2} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            lineHeight: 1,
            display: "block",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontWeight: 500, lineHeight: 1.4, wordBreak: "break-word" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const SectionHeading = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      color: "text.disabled",
      fontWeight: 700,
      letterSpacing: "0.1em",
      fontSize: "0.65rem",
      display: "block",
      mb: 0.5,
      mt: 2,
      px: 1.5,
    }}
  >
    {children}
  </Typography>
);


const TwoColGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    }}
  >
    {children}
  </Box>
);


const PersonalInfoTab = ({
  personalInfo,
  additionalData,
  onModification,
  setNotification,
  dropDownOptions,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Add at the top of the component
  const setUser = useAuthStore(s => s.setUser);
  const currentUser = useAuthStore(s => s.user);

  const { hideWidget, showWidget } = useTawkControl();
  useEffect(() => {
    if (isEditing) hideWidget();
    else showWidget();
  }, [isEditing]);

  const handleSave = async (data) => {
    setLoading(true);
    let payload = { ...data };
    if (payload.profile_picture instanceof File) {
      payload.profile_picture = await fileToBase64(payload.profile_picture);
    } else if (typeof payload.profile_picture === "string") {
      delete payload.profile_picture;
    }
    try {
      const res = await professionalProfileApi.updateProfessionalProfile(payload);
      if (res.status === 200) {
        setUser({
        ...currentUser,
        ...(res.data.full_name && { full_name: res.data.full_name }),
        ...(res.data.profile_picture && { profile_picture: res.data.profile_picture }),
      });
        setNotification("Profile updated successfully", "success");
        onModification();
      }
    } catch (error) {
      Sentry.captureException(error, "Failed to update Personal information")
      setNotification("Failed to update Personal information", "error");
      setErrors(error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CustomLoader />;

  if (isEditing) {
    return (
      <PersonalInfoForm
        data={{ personal_info: personalInfo, ...additionalData }}
        onCancel={() => setIsEditing(false)}
        onSave={handleSave}
        isEditMode
        errors={errors}
        setErrors={setErrors}
        dropDownOptions={dropDownOptions}
      />
    );
  }

  const initials = (additionalData.full_name || "?")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const relocate = personalInfo.willing_to_relocate;

  const regions = personalInfo.preferred_work_regions
    ? commaSeparatedTitleCase(
        Array.isArray(personalInfo.preferred_work_regions)
          ? personalInfo.preferred_work_regions.join(",")
          : personalInfo.preferred_work_regions
      )
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
    : [];

  const socialLinks = additionalData.social_media_links || {};
  const activeSocialLinks = Object.entries(socialLinks).filter(([, v]) => v);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={additionalData.profile_picture? BASE_URL+additionalData.profile_picture : undefined}
            sx={{
              width: 48,
              height: 48,
              bgcolor: "primary.main",
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              {additionalData.full_name || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {additionalData.email}
            </Typography>
          </Box>
        </Box>

        <Tooltip title="Edit personal info">
          <IconButton
            onClick={() => setIsEditing(true)}
            size="small"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: "primary.main",
                color: "white",
                borderColor: "primary.main",
              },
            }}
          >
            <Edit size={16} />
          </IconButton>
        </Tooltip>
      </Box>

      
      <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2.5 }}>

        {/* Identity */}
        <SectionHeading>Identity</SectionHeading>
        <TwoColGrid>
          <InfoRow icon={Phone} label="Phone" value={personalInfo.phone_number || "Not set"} />
          <InfoRow icon={Calendar} label="Date of Birth" value={personalInfo.date_of_birth || "Not set"} />
          <InfoRow icon={Flag} label="Nationality" value={titleCase(personalInfo.nationality) || "Not set"} />
          <InfoRow icon={CreditCard} label="National ID" value={personalInfo.national_id || "Not set"} />
        </TwoColGrid>

        <Divider sx={{ my: 1.5 }} />

        {/* Location */}
        <SectionHeading>Location</SectionHeading>
        <TwoColGrid>
          <InfoRow icon={MapPin} label="City" value={titleCase(personalInfo.city) || "Not set"} />
          <InfoRow icon={Globe} label="Country" value={personalInfo.country || "Not set"} />
          <InfoRow icon={Languages} label="Language" value={titleCase(personalInfo.language) || "Not set"} />
        </TwoColGrid>

        <Divider sx={{ my: 1.5 }} />

        {/* Work Preferences */}
        <SectionHeading>Work Preferences</SectionHeading>
        <TwoColGrid>
          <InfoRow
            icon={Briefcase}
            label="Availability"
            value={personalInfo.availability ? transformString(personalInfo.availability) : "Not set"}
          />
          
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              py: 1.25,
              px: 1.5,
              borderRadius: 2,
              transition: "background 0.15s",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box
              sx={{
                mt: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: "grey.100",
                color: relocate ? "success.main" : "text.secondary",
                flexShrink: 0,
              }}
            >
              {relocate ? <CheckCircle2 size={15} strokeWidth={2} /> : <XCircle size={15} strokeWidth={2} />}
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  lineHeight: 1,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Willing to Relocate
              </Typography>
              <Chip
                label={relocate ? "Yes" : "No"}
                size="small"
                color={relocate ? "success" : "default"}
                variant={relocate ? "filled" : "outlined"}
                sx={{ fontWeight: 600, height: 22, fontSize: "0.72rem" }}
              />
            </Box>
          </Box>
        </TwoColGrid>

        {/* Preferred regions */}
        {regions.length > 0 && (
          <Box sx={{ px: 1.5, mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                display: "block",
                mb: 0.8,
              }}
            >
              Preferred Work Regions
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {regions.map((r) => (
                <Chip
                  key={r}
                  label={r}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Professional Bio */}
        {personalInfo.professional_bio && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <SectionHeading>Professional Bio</SectionHeading>
            <Box
              sx={{
                mx: 1.5,
                mt: 1,
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <MessageSquare
                  size={15}
                  style={{ marginTop: 3, flexShrink: 0, opacity: 0.35 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.7 }}
                >
                  {personalInfo.professional_bio}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Social & Web */}
        {activeSocialLinks.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <SectionHeading>Social & Web</SectionHeading>
            <TwoColGrid>
              {activeSocialLinks.map(([platform, url]) => (
                <Box
                  key={platform}
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    py: 1.25,
                    px: 1.5,
                    borderRadius: 2,
                    textDecoration: "none",
                    color: "inherit",
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      mt: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      bgcolor: "grey.100",
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  >
                    <Globe size={15} strokeWidth={2} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.disabled",
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        lineHeight: 1,
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      {titleCase(platform)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "primary.main",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {url}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </TwoColGrid>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default PersonalInfoTab;